import React, { useEffect, useState, useCallback, useRef } from "react";
import axios from "axios";
import { format } from "date-fns";
import { useLocation, useNavigate } from "react-router-dom";
import { BASE_URL } from "../../config"; // adjust path
import { getAuthHeaders } from "../../services/useAuth"; // adjust path if needed
import { Printer, FileText, ArrowLeftCircle } from "lucide-react";
import * as XLSX from "xlsx";
import CustomButton from "../../pages/CustomButton";

export default function WorkingWith() {
  const location = useLocation();
  const navigate = useNavigate();
  const tableRef = useRef(null);

  // --- Query / state inputs ---
  const query = new URLSearchParams(location.search);
  const queryStateId = query.get("state_id");
  const queryMonth = query.get("month");
  const queryYear = query.get("year");
  const queryEmployeeId = query.get("employee_id");

  const navState = location.state ?? {};
  const stateId = navState.state_id ?? queryStateId ?? "";
  const month = navState.month ?? queryMonth ?? format(new Date(), "M");
  const year = navState.year ?? queryYear ?? format(new Date(), "yyyy");
  const employeeId =
    navState.employee_id ?? navState.employeeId ?? queryEmployeeId ?? "";

  // --- Local UI state ---
  const [isLoading, setIsLoading] = useState(false);
  const [records, setRecords] = useState([]); // rows from API
  const [error, setError] = useState("");
  const [reportMeta, setReportMeta] = useState({
    employeeName: navState.employeeName ?? "",
  });

  // columns for days 1..31
  const days = Array.from({ length: 31 }, (_, i) => i + 1);

  // build API url
const getApiUrl = useCallback(() => {
  const params = new URLSearchParams();
  if (stateId) params.append("state_id", String(stateId));
  if (month) params.append("month", String(month));
  if (year) params.append("year", String(year));
  if (employeeId) params.append("employee_id", String(employeeId));
  params.append("is_report", "true"); 
  return `${BASE_URL}/api/pob-targets/employee-datewise-visit-pob?${params.toString()}`;
}, [stateId, month, year, employeeId]);
  // fetch data
  useEffect(() => {
    const load = async () => {
      setIsLoading(true);
      setError("");
      setRecords([]);

      try {
        const url = getApiUrl();
        const res = await axios.get(url, {
          headers: typeof getAuthHeaders === "function" ? getAuthHeaders() : {},
        });
        const payload = res.data;
        const data = Array.isArray(payload) ? payload : payload?.data ?? [];

        if (!Array.isArray(data) || data.length === 0) {
          setRecords([]);
          setError("No records found");
          return;
        }

        setRecords(data);
        const first = data[0] ?? {};
        setReportMeta((m) => ({
          ...m,
          employeeName:
            m.employeeName || first.employee_name || first.employee || "",
        }));
      } catch (e) {
        console.error(e);
        setError("Failed to load data");
      } finally {
        setIsLoading(false);
      }
    };

    if (stateId || employeeId) load();
    else
      setError("Missing state_id or employee_id in query or navigation state.");
  }, [getApiUrl, stateId, employeeId]);

  // ----- Helpers -----
 const buildDaysString = (record = {}) => {
  // If present_days exists (even if it contains 0)
  if (Array.isArray(record.present_days)) {
    if (record.present_days.length === 0) return "";

    const nums = record.present_days
      .map(Number)
      .filter((n) => !isNaN(n)); // allow 0

    return nums.length
      ? nums.sort((a, b) => a - b).join(", ")
      : record.present_days.map(String).join(", ");
  }

  return "";
};


  const safeNumber = (v) => {
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
  };

  // calculate totals
  const totalPob = records.reduce(
    (s, r) => s + safeNumber(r.pob ?? r.pob_value ?? r.pob_target ?? r.target),
    0
  );
  const totalFrequency = records.reduce(
    (s, r) => s + safeNumber(r.Frequency ?? r.frequency),
    0
  );

  // ----- Export functions -----
  // CSV (legacy/compact format)
  const exportCsv = () => {
    if (!records.length) return;

    const header = [
      "S.No",
      "Employee",
      "Designation",
      "City",
      "Manager",
      "POB Target",
      "Total",
      "Frequency",
    ]
      .concat(days.map((d) => `day_${d}`))
      .concat(days.map((d) => `day_${d}_name`));

    const rows = records.map((r, idx) => {
      const base = [
        idx + 1,
        `"${String(r.employee_name ?? r.employee ?? "").replace(/"/g, '""')}"`,
        `"${String(r.designation ?? "").replace(/"/g, '""')}"`,
        `"${String(r.city_hq ?? r.city ?? "").replace(/"/g, '""')}"`,
        `"${String(r.manager ?? "").replace(/"/g, '""')}"`,
        r.pob_target ?? r.pob ?? r.target ?? "",
        r.total ?? "",
        r.Frequency ?? r.frequency ?? 0,
      ];

      const dayVals = days.map((d) => r[`day_${d}`] ?? "");
      const dayNames = days.map(
        (d) => `"${String(r[`day_${d}_name`] ?? "").replace(/"/g, '""')}"`
      );
      return base.concat(dayVals, dayNames).join(",");
    });

    const csv = [header.join(","), ...rows].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `working_with_${
      reportMeta.employeeName || "report"
    }_${year}_${month}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // XLSX using sheet from visible table
  const exportXlsx = () => {
    if (!records.length) return;

    const header = [
      "S.No",
      "Employee",
      "Designation",
      "City",
      "Manager",
      "Target",
      "POB",
      "Days",
      "Frequency",
    ];
    const data = records.map((r, idx) => [
      idx + 1,
      r.employee_name ?? r.employee ?? "",
      r.designation ?? "",
      r.city_hq ?? r.city ?? "",
      r.manager ?? "",
      r.target ?? r.pob_target ?? r.target_value ?? "",
      r.pob ?? r.pob_value ?? 0,
      buildDaysString(r),
      r.Frequency ?? r.frequency ?? 0,
    ]);

    const ws = XLSX.utils.aoa_to_sheet([header, ...data]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "WorkingWith");
    const fileName = `working_with_${
      reportMeta.employeeName || "report"
    }_${year}_${month}.xlsx`;
    XLSX.writeFile(wb, fileName);
  };

  // ----- Print -----
  const printTable = () => {
    const node = tableRef.current;
    if (!node) return window.print();

    const w = window.open("", "_blank", "width=900,height=700");
    if (!w) return;

    const style = `
      body{font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Arial; margin:18px}
      table{border-collapse: collapse; width:100%}
      th,td{border:1px solid #ddd; padding:6px; font-size:12px; text-align:left}
      thead th{background:#f0fdfa}
      .title{font-weight:600; margin-bottom:8px}
    `;

    w.document.write(
      `<!doctype html><html><head><meta charset="utf-8"><title>Working With</title><style>${style}</style></head><body>`
    );
    // add a header for context
    w.document.write(
      `<div class="title">Working With ${
        reportMeta.employeeName ? ` - ${reportMeta.employeeName}` : ""
      } (${month}/${year})</div>`
    );
    w.document.write(node.innerHTML);
    w.document.write("</body></html>");
    w.document.close();
    w.focus();
    setTimeout(() => w.print(), 300);
  };

  // ----- Render -----
  return (
    <div className="min-h-screen bg-slate-50 p-6">
      {isLoading && (
        <div className="mb-4 text-sm text-slate-600">Loading data...</div>
      )}
      {error && <div className="mb-4 text-sm text-red-600">{error}</div>}

      <div className="max-w-7xl mx-auto">
        {/* Centered heading and date */}
        <div className="flex flex-col items-center justify-center mb-4">
          <div className="text-lg font-semibold text-slate-800 text-center">
            Working With
            {reportMeta.employeeName ? ` - ${reportMeta.employeeName}` : ""} (
            {month}/{year})
          </div>
        </div>

        {/* printable table area */}
        <div
          ref={tableRef}
          className="bg-white rounded-lg shadow-sm overflow-hidden border border-slate-200"
        >
          <div className="border rounded shadow  table-wrapper">
            <table className="min-w-full table-auto divide-y divide-slate-200">
              <thead className="bg-blue-500 text-white">
                <tr>
                  <th className="sticky top-0 z-10 px-3 py-2 text-left text-xs font-medium uppercase tracking-wider text-white border-b border-slate-200 w-12">
                    S.No
                  </th>
                  <th className="sticky top-0 z-10 px-3 py-2 text-left text-xs font-medium uppercase tracking-wider text-white border-b border-slate-200 w-56">
                    Employee
                  </th>
                  <th className="sticky top-0 z-10 px-3 py-2 text-left text-xs font-medium uppercase tracking-wider text-white border-b border-slate-200 w-28">
                    Designation
                  </th>
                  <th className="sticky top-0 z-10 px-3 py-2 text-left text-xs font-medium uppercase tracking-wider text-white border-b border-slate-200 w-36">
                    City
                  </th>
                  <th className="sticky top-0 z-10 px-3 py-2 text-left text-xs font-medium uppercase tracking-wider text-white border-b border-slate-200 w-60">
                    Manager
                  </th>
                  <th className="sticky top-0 z-10 px-3 py-2 text-right text-xs font-medium uppercase tracking-wider text-white border-b border-slate-200 w-36">
                    Target
                  </th>
                  <th className="sticky top-0 z-10 px-3 py-2 text-right text-xs font-medium uppercase tracking-wider text-white border-b border-slate-200 w-36">
                    POB
                  </th>
                  <th className="sticky top-0 z-10 px-3 py-2 text-left text-xs font-medium uppercase tracking-wider text-white border-b border-slate-200">
                    Days
                  </th>
                  <th className="sticky top-0 z-10 px-3 py-2 text-center text-xs font-medium uppercase tracking-wider text-white border-b border-slate-200 w-20">
                    Frequency
                  </th>
                </tr>
              </thead>

              <tbody className="bg-white divide-y divide-slate-100">
                {records.map((rec, idx) => {
                  const employee = rec.employee_name ?? rec.employee ?? "";
                  const designation = rec.designation ?? "";
                  const city = rec.city_hq ?? rec.city ?? "";
                  const manager = rec.manager ?? "";
                  const target =
                    rec.target ?? rec.pob_target ?? rec.target_value ?? "";
                  const pob = rec.total ?? rec.pob_value ?? 0;
                  const daysString = buildDaysString(rec);
                  const frequency = rec.Frequency ?? rec.frequency ?? 0;

                  return (
                    <tr
                      key={idx}
                      className={idx % 2 === 0 ? "bg-white" : "bg-slate-50"}
                    >
                      <td className="px-3 py-2 whitespace-nowrap text-sm text-slate-700 border-b border-slate-100">
                        {idx + 1}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap text-sm text-slate-800 max-w-xs truncate border-b border-slate-100">
                        {employee}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap text-sm text-slate-700 border-b border-slate-100">
                        {designation}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap text-sm text-slate-700 border-b border-slate-100">
                        {city}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap text-sm text-slate-700 border-b border-slate-100">
                        {manager}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap text-sm text-right text-slate-700 border-b border-slate-100">
                        {typeof target === "number"
                          ? target.toLocaleString()
                          : target}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap text-sm text-right text-slate-700 border-b border-slate-100">
                        {Number(pob).toLocaleString()}
                      </td>
                      <td className="px-3 py-2 text-sm text-slate-700 border-b border-slate-100  truncate">
                        {daysString}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap text-sm text-center text-slate-700 border-b border-slate-100">
                        {frequency}
                      </td>
                    </tr>
                  );
                })}

                {/* totals row */}
                <tr className="bg-slate-800 text-white font-semibold">
                  <td
                    className="px-3 py-2 border-t border-slate-700"
                    colSpan={6}
                  ></td>
                  <td className="px-3 py-2 border-t border-slate-700 text-right">
                    {totalPob.toLocaleString()}
                  </td>
                  <td className="px-3 py-2 border-t border-slate-700"></td>
                  <td className="px-3 py-2 border-t border-slate-700 text-center">
                    {totalFrequency || ""}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <div className="mt-4 flex gap-4 justify-center">
          <CustomButton onClick={printTable} label="Print" variant="print">
            🖨
          </CustomButton>
          <CustomButton onClick={exportXlsx} label="Export" variant="export">
            📥
          </CustomButton>
        </div>
      </div>
    </div>
  );
}
